import { jwtDecode, JwtPayload } from "jwt-decode";
import { StateCreator } from "zustand";
import { AuthClient } from "../auth/auth-client";
import { getAuthLocalStorageValue, updateAuthLocalStorage } from "../auth/auth-storage";
import { hasFeature } from "../feature-flags";
import { ApplicationConfiguration } from "../types";

const MOCK_ACCESS_TOKEN_STORAGE_KEY = "@mc/mock/accessToken";

/**
 * @deprecated Direct access to the auth client should be avoided wherever possible.
 * Accessing it directly creates a tight coupling to Okta, which we're moving away from.
 * Use the applicationSettingsStore to get access to most authentication information instead
 */
export let authClient: AuthClient | null;

export type MCAuthClaims = JwtPayload & {
    groups?: string[];
    grp?: string[];
    mcIdpTenantId?: string;
    qxDbUsername?: string;
    tenantId?: string;
    tenantName?: string;
    uid?: string;
    username?: string;
};

type AuthenticationSliceState = {
    accessGroups: Map<string, string[]>;
    accessToken: string | undefined;
    claims: MCAuthClaims | null;
    isAuthenticated: boolean;
    uid: string | null;
};

type AuthenticationSliceActions = {
    setAccessToken: (token: string | undefined) => void;
    setApplicationConfiguration: (applicationConfiguration: ApplicationConfiguration) => Promise<void>;
    setIsAuthenticated: (isAuthenticated: boolean) => void;
};

export type AuthenticationSlice = AuthenticationSliceState & AuthenticationSliceActions;

const initialState: AuthenticationSliceState = {
    accessGroups: new Map<string, string[]>(),
    accessToken: undefined,
    claims: null,
    isAuthenticated: false,
    uid: null,
};

export const createAuthenticationSlice: StateCreator<AuthenticationSlice> = (set) => {
    const setAccessToken = (token: string | undefined) => {
        if (token) {
            const decodedToken = jwtDecode<MCAuthClaims>(token);
            set((currentState) => ({
                accessGroups: new Map(
                    currentState.accessGroups.set(token, decodedToken.groups || decodedToken.grp || []),
                ),
                accessToken: token,
                claims: decodedToken,
                uid: decodedToken.uid,
            }));
        } else {
            set({
                accessGroups: new Map<string, string[]>(),
                accessToken: token,
                claims: null,
                uid: null,
            });
        }
    };
    const setIsAuthenticated = (isAuthenticated: boolean) => set({ isAuthenticated });
    return {
        ...initialState,
        setAccessToken,
        setApplicationConfiguration: async (applicationConfiguration) => {
            // Unsubscribe from the previous auth client
            authClient?.unsubscribe();
            const searchParams = new URLSearchParams(window.location.search);
            const jwt = searchParams.get("token");
            const mcToken = jwt || getAuthLocalStorageValue("mcToken");

            const isMcIdpAuth = hasFeature("commonMcIDPAuthenticationEnabled");
            authClient = new AuthClient({
                applicationConfiguration,
                isMcIdpAuth,
            });

            const shouldBeMocked = !!applicationConfiguration.authentication?.shouldBeMocked;
            if (!shouldBeMocked) {
                if (jwt) {
                    updateAuthLocalStorage({ isIssuedByMc: true, mcToken });
                }

                if (isMcIdpAuth) {
                    await authClient._initMcIdpClient();
                } else {
                    authClient._initOktaClient(shouldBeMocked);
                }

                authClient.subscribe((authState) => {
                    if (authState.isAuthenticated !== undefined) {
                        setIsAuthenticated(authState.isAuthenticated);
                    }

                    if (mcToken) {
                        const currentTime = +new Date() / 1000;
                        const decodedToken = jwtDecode<JwtPayload>(mcToken);
                        setAccessToken(mcToken);

                        if ((decodedToken?.exp ?? 0) > currentTime) {
                            setIsAuthenticated(true);
                        }
                    }

                    if (authState.accessToken && !mcToken) {
                        setAccessToken(authState.accessToken);
                    }
                });
                setAccessToken(undefined);
                setIsAuthenticated(false);
            } else {
                authClient._initOktaClient(shouldBeMocked);
                const accessToken = window?.localStorage?.getItem(MOCK_ACCESS_TOKEN_STORAGE_KEY) ?? undefined;
                setAccessToken(accessToken);
                setIsAuthenticated(true);
            }
        },
        setIsAuthenticated,
    };
};
